import React, { useState, useEffect } from "react";
import FadeAlert, {
  FadeAlertType,
} from "../../components/FadeAlert/FadeAlert.tsx";
import PolicyService from "../../services/PolicyService/PolicyService.ts";
import { useAuth } from "../../provider/AuthProvider.tsx";
import { normalizeDocumentSearchProperties } from "../../utils/NormalizationUtil.ts";
import InsuranceService from "../../services/InsuranceService/InsuranceService.ts";
import SelectGroup from "../../components/Forms/SelectGroup/SelectGroup.tsx";
import DocumentSearchDataGrid from "../../components/DataTables/DocumentSearchDataGrid.tsx";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb.tsx";
import SectionHeader from "../../components/SectionHeader/SectionHeader.tsx";
import { inputClass, labelClass } from "../../constants/formStyles";

const DOCUMENT_TYPES = {
  0: "Полици",
  1: "Сметки",
  2: "Стикери",
  3: "Други ЦК",
  4: "СЗК",
};

const DocumentSearch: React.FC = () => {
  const [formState, setFormState] = useState({
    documentType: 0,
    insuranceCompany: "",
    insuranceType: "",
    documentNumber: "",
  });
  const [alert, setAlert] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<FadeAlertType>();
  const { jwt } = useAuth();
  const [insuranceCompanies, setInsuranceCompanies] = useState<{
    [key: string]: string;
  }>({});
  const [policyTypes, setPolicyTypes] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [policies, setPolicies] = useState<any[]>([]);

  useEffect(() => {
    setFormState({
      documentType: 0,
      documentNumber: "",
      insuranceType: "",
      insuranceCompany: "",
    });
    setPolicies([]); // Clean grid records on link transitions
    setAlert(null);
    setAlertType(undefined);
  }, [location.pathname]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setIsLoading(true);
        const [companies, policyTypesRes] = await Promise.all([
          InsuranceService.getAllInsuranceCompanies(jwt),
          InsuranceService.getAllPolicyTypes(jwt),
        ]);
        setInsuranceCompanies(companies as { [key: string]: string });
        setPolicyTypes(policyTypesRes as { [key: string]: string });
      } catch (err: any) {
        throw new Error(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [jwt]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formState.documentNumber) {
      setAlert("Моля, въведете номер на документа");
      setAlertType(FadeAlertType.error);
      return;
    }

    // Clear previous results before new search
    setPolicies([]);
    setAlert(null);
    setAlertType(undefined);

    try {
      setIsLoading(true);
      const result = await PolicyService.fetchDocument(
        formState.documentNumber,
        formState.insuranceCompany,
        formState.insuranceType,
        formState.documentType,
        jwt
      );
      const normalizedPolicies = Object.values(result).map((policy) =>
        normalizeDocumentSearchProperties(policy)
      );
      setPolicies(normalizedPolicies);
    } catch (error) {
      setAlert("Грешка при зареждането на полици. " + error.message);
      setAlertType(FadeAlertType.error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Breadcrumb pageName={"Търсене по номер на документ"} />
      <FadeAlert
        alert={alert}
        type={alertType}
        onClose={() => setAlert(null)}
      />

      <div className="border-stroke shadow-default dark:border-strokedark dark:bg-boxdark rounded-sm border bg-white">
        <SectionHeader title="Търсене по номер на документ" />

        <form onSubmit={handleSearch} className="p-3">
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
            <div>
              <label className={labelClass}>Тип документ</label>
              <SelectGroup
                value={formState.documentType}
                placeholder="Изберете тип документ"
                options={DOCUMENT_TYPES}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, documentType: value }))
                }
                name="documentType"
              />
            </div>
            <div>
              <label className={labelClass}>Номер на документ</label>
              <input
                type="text"
                name="documentNumber"
                value={formState.documentNumber}
                onChange={(e) =>
                  setFormState((prev) => ({
                    ...prev,
                    documentNumber: e.target.value,
                  }))
                }
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>ЗК</label>
              <SelectGroup
                value={formState.insuranceCompany}
                placeholder="Изберете компания"
                options={insuranceCompanies}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, insuranceCompany: value }))
                }
                name="insuranceCompany"
              />
            </div>
            <div>
              <label className={labelClass}>Вид Полица</label>
              <SelectGroup
                value={formState.insuranceType}
                placeholder="Изберете вид полица"
                options={policyTypes}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, insuranceType: value }))
                }
                name="insuranceType"
              />
            </div>
          </div>

          <div className="mt-3 flex justify-center">
            <button
              type="submit"
              className="bg-primary flex w-1/6 justify-center rounded px-3 py-1.5 text-xs font-medium text-white hover:bg-opacity-90"
            >
              Покажи
            </button>
          </div>
        </form>
      </div>

      {policies.length > 0 && (
        <div className="bg-primary/5 text-primary border-primary/20 mt-3 flex items-center gap-1.5 rounded border px-3 py-1.5 text-[11px] font-medium">
          <i className="fa-solid fa-circle-info animate-pulse text-xs" />
          <span>
            Кликнете върху избран ред от таблицата, за да заредите подробните
            данни и падежи по полицата.
          </span>
        </div>
      )}

      <DocumentSearchDataGrid
        policies={policies}
        isLoading={isLoading}
        documentType={formState.documentType}
      />
    </>
  );
};

export default DocumentSearch;
